import { useEffect, useRef, useState } from 'react';
import { useData } from '../../contexts/DataContext';

const BASE_URL = import.meta.env.VITE_CHATWOOT_BASE_URL || 'https://chat.trypopms.com';
const WEBSITE_TOKEN = import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN || 'oQPsmgLU1baxkMjEhVRkyEFS';
const PROVEIA_AVATAR = '/assets/proveminer-img/proveia-avatar.png';

const STORAGE_KEY_CORNER = 'proveia_fab_corner';
const VALID_CORNERS = ['br', 'bl', 'tr', 'tl'];
const DRAG_THRESHOLD_PX = 6;
const CLICK_MAX_DURATION_MS = 350;
const FAB_SIZE_DESKTOP = 64;
const FAB_SIZE_MOBILE = 48;
const MARGIN_X = 20;
const MARGIN_TOP = 80;
const MARGIN_BOTTOM_DESKTOP = 20;
const MARGIN_BOTTOM_MOBILE = 88;

function readCorner() {
    try {
        const v = localStorage.getItem(STORAGE_KEY_CORNER);
        return VALID_CORNERS.includes(v) ? v : 'br';
    } catch { return 'br'; }
}

function writeCorner(c) {
    try { localStorage.setItem(STORAGE_KEY_CORNER, c); } catch { /* noop */ }
}

function nearestCorner(x, y) {
    if (typeof window === 'undefined') return 'br';
    const w = window.innerWidth, h = window.innerHeight;
    return (y < h / 2 ? 't' : 'b') + (x < w / 2 ? 'l' : 'r');
}

export default function ChatwootWidget() {
    const { user } = useData();
    const [ready, setReady] = useState(false);
    const [open, setOpen] = useState(false);

    // Load Chatwoot SDK once on mount — ocultar el globo nativo
    useEffect(() => {
        if (!WEBSITE_TOKEN) return;

        window.chatwootSettings = {
            hideMessageBubble: true,
            position: 'right',
            locale: 'es',
            type: 'standard',
            launcherTitle: 'Soporte ProveIA',
        };

        const script = document.createElement('script');
        script.src = `${BASE_URL}/packs/js/sdk.js`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            if (window.chatwootSDK) {
                window.chatwootSDK.run({ websiteToken: WEBSITE_TOKEN, baseUrl: BASE_URL });
            }
        };
        document.body.appendChild(script);

        const onReady = () => {
            if (typeof window.$chatwoot?.toggleBubbleVisibility === 'function') {
                window.$chatwoot.toggleBubbleVisibility('hide');
            }
            setReady(true);
        };
        window.addEventListener('chatwoot:ready', onReady);

        return () => {
            script.remove();
            window.removeEventListener('chatwoot:ready', onReady);
        };
    }, []);

    // Identificar usuario cuando esté logueado
    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        let attempts = 0;

        const identify = () => {
            if (cancelled) return;
            if (window.$chatwoot && typeof window.$chatwoot.setUser === 'function') {
                try {
                    window.$chatwoot.setUser(String(user.id), {
                        email: user.email || '',
                        name: user.fullName || user.name || user.email || '',
                        phone_number: user.phone || user.phoneNumber || '',
                    });
                } catch (err) {
                    if (import.meta.env.VITE_ENABLE_LOGGING === 'true') {
                        // eslint-disable-next-line no-console
                        console.warn('[ChatwootWidget] setUser failed', err);
                    }
                }
                return;
            }
            if (attempts++ < 20) setTimeout(identify, 500);
        };

        identify();
        return () => { cancelled = true; };
    }, [user]);

    const handleToggle = () => {
        if (window.$chatwoot) {
            window.$chatwoot.toggle();
            setOpen(prev => !prev);
        }
    };

    // Auto-ocultar al hacer scroll hacia abajo, reaparecer al subir.
    const [hidden, setHidden] = useState(false);
    const lastY = useRef(0);
    useEffect(() => {
        lastY.current = window.scrollY || 0;
        const onScroll = () => {
            const y = window.scrollY || 0;
            const delta = y - lastY.current;
            if (Math.abs(delta) < 6) return;
            if (y < 80) setHidden(false);
            else if (delta > 0) setHidden(true);
            else setHidden(false);
            lastY.current = y;
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // === Drag-to-reposition con snap a las 4 esquinas + persistencia ===
    const [corner, setCorner] = useState(readCorner);
    const [dragPos, setDragPos] = useState(null);
    const dragStartRef = useRef(null);
    const latestPosRef = useRef(null);

    const onPressStart = (e) => {
        const p = e.touches ? e.touches[0] : e;
        const start = { x: p.clientX, y: p.clientY, time: Date.now() };
        dragStartRef.current = start;
        latestPosRef.current = start;
        setDragPos({ x: p.clientX, y: p.clientY });
    };

    useEffect(() => {
        if (!dragPos) return;
        const onMove = (e) => {
            const p = e.touches ? e.touches[0] : e;
            latestPosRef.current = { x: p.clientX, y: p.clientY };
            setDragPos({ x: p.clientX, y: p.clientY });
            if (e.cancelable && e.touches) e.preventDefault();
        };
        const onUp = () => {
            const start = dragStartRef.current;
            const last = latestPosRef.current;
            const dt = Date.now() - start.time;
            const dx = last.x - start.x;
            const dy = last.y - start.y;
            const movedFar = Math.hypot(dx, dy) > DRAG_THRESHOLD_PX;
            setDragPos(null);
            if (!movedFar && dt < CLICK_MAX_DURATION_MS) {
                handleToggle();
            } else {
                const newCorner = nearestCorner(last.x, last.y);
                setCorner(newCorner);
                writeCorner(newCorner);
            }
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('touchend', onUp);
        window.addEventListener('touchcancel', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
            window.removeEventListener('touchcancel', onUp);
        };
    }, [dragPos !== null]);

    const isDragging = !!dragPos;
    const isHidden = hidden && !open && !isDragging;
    const isRightSide = corner.endsWith('r');
    const isMobile = typeof window !== 'undefined'
        && window.matchMedia && window.matchMedia('(max-width: 1023px)').matches;
    const size = isMobile ? FAB_SIZE_MOBILE : FAB_SIZE_DESKTOP;
    const half = size / 2;

    let positionStyle;
    if (isDragging) {
        positionStyle = { left: dragPos.x - half, top: dragPos.y - half };
    } else {
        const bot = isMobile ? MARGIN_BOTTOM_MOBILE : MARGIN_BOTTOM_DESKTOP;
        positionStyle = corner === 'br' ? { bottom: bot, right: MARGIN_X }
            : corner === 'bl' ? { bottom: bot, left: MARGIN_X }
            : corner === 'tr' ? { top: MARGIN_TOP, right: MARGIN_X }
            : { top: MARGIN_TOP, left: MARGIN_X };
    }

    let transform = '';
    if (isHidden) transform = isRightSide ? 'translateX(140%)' : 'translateX(-140%)';
    else if (open) transform = 'scale(1.08)';

    return (
        <>
        <style>{`
          .proveia-fab {
            transition: top 0.32s cubic-bezier(0.34, 1.56, 0.64, 1),
                        bottom 0.32s cubic-bezier(0.34, 1.56, 0.64, 1),
                        left 0.32s cubic-bezier(0.34, 1.56, 0.64, 1),
                        right 0.32s cubic-bezier(0.34, 1.56, 0.64, 1),
                        box-shadow 0.2s,
                        transform 0.25s ease;
            touch-action: none;
            user-select: none;
            -webkit-user-select: none;
          }
          .proveia-fab.is-dragging {
            transition: box-shadow 0.2s, transform 0.1s ease !important;
            cursor: grabbing !important;
          }
          .proveia-fab.is-hidden { pointer-events: none; }
          @media (max-width: 1023px) {
            .proveia-fab { width: 48px !important; height: 48px !important; }
            .proveia-fab img { width: 48px !important; height: 48px !important; }
          }
        `}</style>
        <button
            onMouseDown={onPressStart}
            onTouchStart={onPressStart}
            title="Soporte ProveIA — arrastrame para moverme a otra esquina"
            aria-label="Soporte ProveIA — arrastrá para reposicionar, click para abrir"
            aria-hidden={isHidden}
            tabIndex={isHidden ? -1 : 0}
            className={`proveia-fab${isHidden ? ' is-hidden' : ''}${isDragging ? ' is-dragging' : ''}`}
            style={{
                position: 'fixed',
                zIndex: 9999,
                width: `${FAB_SIZE_DESKTOP}px`,
                height: `${FAB_SIZE_DESKTOP}px`,
                borderRadius: '50%',
                border: 'none',
                padding: 0,
                cursor: isDragging ? 'grabbing' : 'grab',
                background: 'transparent',
                boxShadow: open
                    ? '0 0 0 3px #f97316, 0 8px 24px rgba(249,115,22,0.5)'
                    : isDragging
                        ? '0 12px 32px rgba(0,0,0,0.45), 0 0 0 2px rgba(249,115,22,0.4)'
                        : '0 4px 16px rgba(0,0,0,0.35)',
                transform,
                opacity: ready || !WEBSITE_TOKEN ? 1 : 0.6,
                ...positionStyle,
            }}
        >
            <img
                src={PROVEIA_AVATAR}
                alt="ProveIA"
                style={{
                    width: `${FAB_SIZE_DESKTOP}px`,
                    height: `${FAB_SIZE_DESKTOP}px`,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    display: 'block',
                    pointerEvents: 'none',
                    userSelect: 'none',
                }}
                draggable={false}
            />
        </button>
        </>
    );
}
